/*
 |--------------------------------------------------------------------------
 | HTTP Service
 |--------------------------------------------------------------------------
 */

import axios from "axios";
import { SnackbarProgrammatic as Snackbar } from "buefy";
import _ from "lodash";

const http = axios.create({
    headers: {
        "X-Requested-With": "XMLHttpRequest",
        "X-CSRF-TOKEN": window.Laravel.csrfToken,
    },
});

/**
 * Add interceptor
 */
http.interceptors.response.use(
    response => response,
    errorResponseHandler,
);

/**
 * Create an error response handler
 */
function errorResponseHandler(error: any) {

    // Find the message in the response
    let message = findMessage(error);
    let status = findStatus(error.response);

    // Show the message
    Snackbar.open({
        message: message,
        type: "is-danger"
    });

    // If auth error, redirect to login in 1.5 seconds
    if (status == 419 || (status == 401 && message.includes("Unauthenticated"))) {
        setTimeout(() => {
            window.location.href = window.route("login");
        }, 1500);
    }

    // If employee DAY_NOT_STARTED error, reload the page
    if (status == 400 && findCustomErrorCode(_.get(error, "response", null), "DAY_NOT_STARTED")) {
        window.location.reload();
    }

    // Reject the Promise
    return Promise.reject(error);
}

export default http;

/*
|--------------------------------------------------------------------------
| Utils
|--------------------------------------------------------------------------
|
*/

/**
 * Find the message of an error
 */
function findMessage(error: any): string {
    // Default message
    let defaultMessage = "Whoops! An error has occurred.";

    // No response returned, this may be a big error
    if (!_.isObject(error.response)) {
        Sentry.captureException(error);
        return defaultMessage;
    }

    if (!_.isObject(error.response.data)) {
        return defaultMessage;
    }

    if (_.isString(error.response.data.message)) {
        return error.response.data.message;
    }

    if (_.isObject(error.response.data.errors) && _.isString(error.response.data.errors.message)) {
        return error.response.data.errors.message;
    }

    return defaultMessage;
}

/**
 * Find the status of a response
 */
function findStatus(response: any): number | null {
    if (!_.isObject(response)) {
        return null;
    }

    return (response as any).status;
}

/**
 * Find a custom error code
 */
function findCustomErrorCode(response: any, code: string): boolean {

    if (response == null) {
        return false;
    }

    return _.get(response, 'data.error_code') == code;
}
